/**
 * YourBeans Merchant Authentication Service
 *
 * Handles merchant authentication against the YourBeans platform API.
 * Merchants use their existing YourBeans store credentials - no separate account.
 *
 * Uses a same-origin Vercel proxy (/api/auth-vendor) to avoid CORS; the proxy
 * calls app.yourbeans.com.au server-side.
 */

// Use Vercel proxy to avoid CORS (calls the YourBeans platform server-side)
const AUTH_PROXY_URL = '/api/auth-vendor';
// Same proxy is used to validate a token (a 200 means the session is still good).
const DASHBOARD_PROXY_URL = '/api/vendor-dashboard';

export interface VendorAuthResponse {
  success: boolean;
  token?: string;
  vendor_id?: number;
  email?: string;
  name?: string;
  store_name?: string;
  avatar_url?: string;
  package?: string;
  expires_at?: number;
  error?: string;
  message?: string;
}

export interface VendorSession {
  token: string;
  vendorId: number;
  email: string;
  name: string;
  storeName: string;
  avatarUrl: string;
  package: string;
  expiresAt: number;
}

const STORAGE_KEY = 'yourbeans_vendor_session';

/**
 * Authenticate vendor with MyShop.coffee credentials
 */
export async function authenticateVendor(
  email: string,
  password: string
): Promise<VendorAuthResponse> {
  try {
    // Use Vercel proxy to avoid CORS issues
    const response = await fetch(AUTH_PROXY_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ email, password }),
    });

    const data: VendorAuthResponse = await response.json();

    if (data.success && data.token) {
      // Save session to localStorage
      const session: VendorSession = {
        token: data.token,
        vendorId: data.vendor_id!,
        email: data.email!,
        name: data.name!,
        storeName: data.store_name || '',
        avatarUrl: data.avatar_url || '',
        package: data.package || 'none',
        expiresAt: data.expires_at!,
      };
      localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    }

    return data;
  } catch (error) {
    console.error('YourBeans auth error:', error);
    return {
      success: false,
      error: 'network_error',
      message: 'Unable to connect to YourBeans. Please check your internet connection.',
    };
  }
}

/**
 * Check if current session is valid
 */
export async function checkVendorSession(): Promise<VendorSession | null> {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    return null;
  }

  try {
    const session: VendorSession = JSON.parse(stored);

    // Check if expired locally first
    if (session.expiresAt < Date.now() / 1000) {
      clearVendorSession();
      return null;
    }

    // Optionally verify with server (can be skipped for performance)
    // For now, trust local expiry check
    return session;
  } catch {
    clearVendorSession();
    return null;
  }
}

/**
 * Verify session with the YourBeans platform (optional, for critical operations).
 * The dashboard proxy returns success only when the bearer token still validates.
 */
export async function verifyVendorSession(): Promise<VendorSession | null> {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    return null;
  }

  try {
    const session: VendorSession = JSON.parse(stored);

    const response = await fetch(
      `${DASHBOARD_PROXY_URL}?token=${encodeURIComponent(session.token)}`
    );
    const data = await response.json();

    if (data.success) {
      return session;
    } else {
      clearVendorSession();
      return null;
    }
  } catch {
    // Network error - keep session for offline use
    const session: VendorSession = JSON.parse(stored);
    if (session.expiresAt > Date.now() / 1000) {
      return session;
    }
    clearVendorSession();
    return null;
  }
}

/**
 * Clear vendor session (logout)
 */
export function clearVendorSession(): void {
  localStorage.removeItem(STORAGE_KEY);
}

/**
 * Get current vendor session without verification
 */
export function getVendorSession(): VendorSession | null {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (!stored) {
    return null;
  }

  try {
    const session: VendorSession = JSON.parse(stored);

    // Check local expiry
    if (session.expiresAt < Date.now() / 1000) {
      clearVendorSession();
      return null;
    }

    return session;
  } catch {
    clearVendorSession();
    return null;
  }
}

/**
 * Get the auth token for API requests
 */
export function getVendorToken(): string | null {
  const session = getVendorSession();
  return session?.token || null;
}
